/** @format */

import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useCart } from "../contexts/CartContext";
import iconLogo from "../assets/icon.png";
import { shouldShowIOSWebViewAlert } from "../utils/deviceDetection";
import {
  HomeIcon,
  BookOpenIcon,
  AcademicCapIcon,
  SparklesIcon,
  PlayIcon,
  UserGroupIcon,
  VideoCameraIcon,
  ChatBubbleLeftRightIcon,
  UserIcon,
  ShoppingBagIcon,
  ShoppingCartIcon,
  CreditCardIcon,
  ClipboardDocumentListIcon,
  CogIcon,
  ChartBarIcon,
  GiftIcon,
  BanknotesIcon,
  UserCircleIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  MagnifyingGlassIcon,
  DocumentTextIcon,
  LightBulbIcon,
  BuildingOfficeIcon,
  CalendarIcon,
  ChatBubbleBottomCenterTextIcon,
  BookmarkIcon,
  TruckIcon,
  ShieldCheckIcon,
  CurrencyDollarIcon,
  WrenchScrewdriverIcon,
  PhotoIcon,
  HeartIcon
} from "@heroicons/react/24/outline";
import MobileBottomNav from "./MobileBottomNav";
import MobileFABs from "./MobileFABs";
import EmailVerificationWarning from "./EmailVerificationWarning";


interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children: _children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser, logout } = useAuth();
  const { cartItems } = useCart();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Build navigation without using logical AND operators
  const homeNav = (() => {
    let result: any[] = [];
    let isMobile = false;
    if (typeof window !== "undefined") {
      isMobile = window.matchMedia("(max-width: 767px)").matches;
    }

    let includeLandingPage = true;
    if (currentUser) {
      if (isMobile) {
        includeLandingPage = false;
      }
    }

    if (includeLandingPage) {
      result = [{ name: "Landing Page", href: "/", icon: HomeIcon, group: "home" }];
    }
    return result;
  })();

  const baseNavigation = [
    ...homeNav,
    { name: "Dashboard", href: "/app", icon: HomeIcon, group: "home" },
    { name: "My Courses", href: "/app/courses", icon: BookOpenIcon, group: "learning" },
    { name: "My Books", href: "/app/books", icon: DocumentTextIcon, group: "learning" },
    { name: "Profstudymate", href: "/app/ai-learning", icon: SparklesIcon, group: "learning" },
    { name: "Practice", href: "/app/practice", icon: PlayIcon, group: "learning" },
    
    { name: "Classrooms", href: "/app/classrooms", icon: BuildingOfficeIcon, group: "live" },
    { name: "Live Meetings", href: "/app/live", icon: VideoCameraIcon, group: "live" },
    { name: "Study Forums", href: "/app/forums", icon: ChatBubbleBottomCenterTextIcon, group: "live" },
    { name: "Mentorship", href: "/app/mentorship", icon: UserIcon, group: "live" },
    
    { name: "General Feed", href: "/app/feed", icon: ChatBubbleLeftRightIcon, group: "community" },
    { name: "Blog", href: "/app/blog", icon: DocumentTextIcon, group: "community" },
    { name: "Free Resources", href: "/app/free-resources", icon: LightBulbIcon, group: "community" },
    { name: "Gallery", href: "/gallery", icon: PhotoIcon, group: "community" },
    { name: "Testimonials", href: "/testimonials", icon: HeartIcon, group: "community" },
    
    // Hide entire shopping group on iOS WebView
    ...(shouldShowIOSWebViewAlert() ? [] : [
      { name: "Shop(Books, Materials)", href: "/app/shop", icon: ShoppingBagIcon, group: "shopping" },
      { name: "Courses", href: "/app/catalog", icon: AcademicCapIcon, group: "shopping" },
      { name: "My Orders", href: "/app/orders", icon: ClipboardDocumentListIcon, group: "shopping" },
      { name: `Cart (${cartItems.length})`, href: "/app/cart", icon: ShoppingCartIcon, group: "shopping" },
      { name: "Order Validation", href: "/app/order-validation", icon: ShieldCheckIcon, group: "shopping" },
    ]),
    
    { name: "Affiliate Program", href: "/affiliate", icon: GiftIcon, group: "earnings" },
    { name: "Withdrawals", href: "/withdraw", icon: BanknotesIcon, group: "earnings" },
    
    { name: "Inbox", href: "/app/inbox", icon: ChatBubbleLeftRightIcon, group: "account" },
    { name: "Profile", href: "/profile", icon: UserCircleIcon, group: "account" },
    { name: "Legal Info", href: "/legal", icon: DocumentTextIcon, group: "account" },
  ];

  // Conditionally add admin link without using && in expressions
  const normalizedRole = (currentUser?.role || "").toString().trim().toLowerCase();
  let navigation = baseNavigation.slice();
  let shouldShowAdmin = false;
  if (normalizedRole === "admin") {
    shouldShowAdmin = true;
  } else {
    if (normalizedRole === "author") {
      if (currentUser?.status !== "pending") {
        shouldShowAdmin = true;
      }
    }
  }
  if (shouldShowAdmin) {
    navigation.unshift({ name: "Admin Panel", href: "/app/admin", icon: CogIcon, group: "home" } as any);
  }

  const isActiveRoute = (path: string) => {
    if (path === "/app") {
      return location.pathname === "/app";
    }
    if (path === "/") {
      return location.pathname === "/";
    }
    return location.pathname.startsWith(path);
  };

  const flatNavigation = navigation.flat();
  // Compute unread total badge for Inbox (optional simple localStorage/source)
  // For now, show no count in label; could be enhanced by a hook subscribing to dm_threads.
  const groupedNavigation = {
    home: flatNavigation.filter((item) => item.group === "home"),
    learning: flatNavigation.filter((item) => item.group === "learning"),
    shopping: flatNavigation.filter((item) => item.group === "shopping"),
    live: flatNavigation.filter((item) => item.group === "live"),
    community: flatNavigation.filter((item) => item.group === "community"),
    earnings: flatNavigation.filter((item) => item.group === "earnings"),
    account: flatNavigation.filter((item) => item.group === "account"),
  } as const;

  const groupLabels = {
    home: "Home & Overview",
    learning: "Learning Hub",
    live: "Live Learning",
    community: "Community & Content",
    shopping: "Shopping & Orders",
    earnings: "Earnings & Affiliates",
    account: "Account & Settings",
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile top bar */}
      <div className="lg:hidden sticky top-0 z-40 bg-white border-b border-gray-200 px-4 py-3">
        <div className="flex items-center justify-between">
          <button
            onClick={() => setMobileNavOpen(true)}
            className="p-2 text-gray-600 hover:text-gray-900 rounded-lg"
          >
            <Bars3Icon className="h-6 w-6" />
          </button>
          <div className="flex items-center">
            <img src={iconLogo} alt="ProfStudyMate Logo" className="w-8 h-8 mr-2" />
            <span className="text-lg font-semibold text-gray-900">ProfStudyMate</span>
          </div>
          <div className="flex items-center space-x-2">
            {/* Cart Icon - Hidden on iOS WebView */}
            {!shouldShowIOSWebViewAlert() && (
              <button
                onClick={() => navigate('/app/cart')}
                className="relative p-2 text-gray-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
              >
                <ShoppingCartIcon className="h-5 w-5" />
                {cartItems.length > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-primary-600 text-white text-xs rounded-full flex items-center justify-center">
                    {cartItems.length}
                  </span>
                )}
              </button>
            )}
            
            {/* Profile Icon */}
            <Link
              to="/profile"
              className="p-2 text-gray-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
            >
              <UserCircleIcon className="h-5 w-5" />
            </Link>
          </div>
        </div>
      </div>

      {/* Mobile navigation overlay */}
      {mobileNavOpen ? (
        <div className="fixed inset-0 z-[70] lg:hidden">
          <div className="relative flex w-full h-screen flex-col bg-white z-[70] shadow-2xl">
            <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200">
              <div className="flex items-center">
                <img src={iconLogo} alt="ProfStudyMate Logo" className="w-8 h-8 mr-3" />
                <span className="text-lg font-semibold text-gray-900">ProfStudyMate</span>
              </div>
              <button
                onClick={() => setMobileNavOpen(false)}
                className="p-2 text-gray-600 hover:text-gray-900 rounded-lg"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <nav className="px-4 py-4 space-y-6 overflow-x-hidden overflow-y-auto">
              {Object.entries(groupedNavigation).map(([groupKey, items]) => (
                <div key={groupKey}>
                  <h3 className="px-3 mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    {groupLabels[groupKey as keyof typeof groupLabels]}
                  </h3>
                  <div className="space-y-1">
                    {items.map((item) => (
                      <Link
                        key={item.name}
                        to={item.href}
                        onClick={() => setMobileNavOpen(false)}
                        className={`flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          isActiveRoute(item.href)
                            ? "bg-primary-100 text-primary-700"
                            : "text-gray-700 hover:bg-gray-100"
                        }`}
                      >
                        <div className="flex items-center">
                          <item.icon className="h-5 w-5 mr-3" />
                          {item.name}
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
            </nav>

            <div className="border-t border-gray-200 p-4 flex-shrink-0">
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center mr-3">
                  <span className="text-primary-600 font-semibold text-sm">
                    {currentUser?.name?.charAt(0) || "S"}
                  </span>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {currentUser?.name || "Student"}
                  </p>
                  <p className="text-xs text-gray-500">
                    {currentUser?.role || "Guest"}
                  </p>
                </div>
              </div>
              <button
                onClick={async () => { await handleLogout(); setMobileNavOpen(false); }}
                className="flex items-center w-full px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowRightOnRectangleIcon className="h-5 w-5 mr-3" />
                Sign Out
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Desktop sidebar (always visible on desktop) */}
        <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
          <div className="flex flex-col h-full bg-white border-r border-gray-200">
            <div className="flex items-center h-16 px-6 border-b border-gray-200 flex-shrink-0">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center mr-3">
                <img src={iconLogo} alt="ProfStudy Logo" className="w-8 h-8 rounded-lg" />
              </div>
              <span className="text-lg font-semibold text-gray-900">
                ProfStudyMate
              </span>
            </div>

            <nav className="flex-1 px-4 py-6 space-y-6 overflow-y-auto">
              {Object.entries(groupedNavigation).map(([groupKey, items]) => (
                <div key={groupKey}>
                  <h3 className="px-3 mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    {groupLabels[groupKey as keyof typeof groupLabels]}
                  </h3>
                  <div className="space-y-1">
                    {items.map((item) => (
                        <Link
                          key={item.name}
                          to={item.href}
                          className={`flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                            isActiveRoute(item.href)
                              ? "bg-primary-100 text-primary-700"
                              : "text-gray-700 hover:bg-gray-100"
                          }`}
                        >
                          <div className="flex items-center">
                            <item.icon className="h-5 w-5 mr-3" />
                            {item.name}
                          </div>
                        </Link>
                    ))}
                  </div>
                </div>
              ))}
            </nav>

            <div className="border-t border-gray-200 p-4 flex-shrink-0">
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center mr-3">
                  <span className="text-primary-600 font-semibold text-sm">
                    {currentUser?.name?.charAt(0) || "S"}
                  </span>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {currentUser?.name || "Student"}
                  </p>
                  <p className="text-xs text-gray-500">
                    {currentUser?.role || "Guest"}
                  </p>
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center w-full px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowRightOnRectangleIcon className="h-5 w-5 mr-3" />
                Sign Out
              </button>
            </div>
          </div>
        </div>

      {/* Main content area */}
      <div className="min-h-screen lg:pl-64">
        {/* Email Verification Warning for Dashboard */}
        <EmailVerificationWarning location="header" />
        
        {/* Add bottom padding on mobile to avoid being covered by bottom nav */}
        <main className={`${location.pathname === "/app" ? "p-2 sm:p-3 lg:p-4" : "p-3 sm:p-4 lg:p-6"} pb-24 lg:pb-4`}>
          {_children}
        </main>
      </div>

      {/* Mobile Bottom Navigation */}
      <div className="lg:hidden">
        <MobileBottomNav />
      </div>

      {/* Mobile Floating Action Buttons */}
      <div className="lg:hidden">
        <MobileFABs />
      </div>
    </div>
  );
};

export default Layout;
